import type { TFunction } from 'i18next';

import type { DiscoveredRelease, DiscoveredTitle, ReleaseContents } from './types';

/// Reading what a version holds, for the places that show versions.
///
/// A person looking for a programme is looking for the season, not for episode four of it. What
/// the server read out of the release name is the only thing that says which of the two a
/// posting is, and these turn it into an order and a sentence.

/// Where a version belongs in a list: everything there is at the top, one episode at the bottom.
export function packRank(contents?: ReleaseContents): number {
  if (!contents) {
    return 3;
  }

  if (contents.isComplete || contents.seasons.length > 1) {
    return 0;
  }

  return contents.scope === 'Season' ? 1 : contents.scope === 'Episode' ? 2 : 3;
}

/// The order versions are shown in: the most of a programme first, then the largest picture.
export function byWhatIsInside(first: DiscoveredRelease, second: DiscoveredRelease): number {
  return (
    packRank(first.contents) - packRank(second.contents)
    || (second.contents?.seasons.length ?? 0) - (first.contents?.seasons.length ?? 0)
    || (second.height ?? 0) - (first.height ?? 0)
    || second.sizeInBytes - first.sizeInBytes
  );
}

/// What one version holds, in a sentence. Empty where its name said nothing, which is most films.
export function contentsLabel(contents: ReleaseContents | undefined, t: TFunction): string {
  if (!contents || contents.scope === 'Unknown') {
    return '';
  }

  if (contents.scope === 'Episode' && contents.seasons.length === 1) {
    const season = contents.seasons[0];

    return contents.episodes.length === 1
      ? t('discover.contents.episode', { season, episode: contents.episodes[0] })
      : t('discover.contents.episodes', { season, episodes: numbers(contents.episodes) });
  }

  if (contents.seasons.length === 0) {
    return t('discover.contents.complete');
  }

  const seasons =
    contents.seasons.length === 1
      ? t('discover.contents.season', { season: contents.seasons[0] })
      : t('discover.contents.seasons', { seasons: numbers(contents.seasons) });

  return contents.isComplete ? `${seasons} · ${t('discover.contents.complete')}` : seasons;
}

/// Every season any of these versions holds, as one line. What can be had, at a glance.
export function seasonsOnOffer(releases: DiscoveredRelease[], t: TFunction): string {
  const seasons = [...new Set(releases.flatMap((release) => release.contents?.seasons ?? []))].sort(
    (first, second) => first - second,
  );

  if (seasons.length === 0) {
    return releases.some((release) => release.contents?.isComplete)
      ? t('discover.contents.complete')
      : '';
  }

  return t('discover.contents.available', { seasons: numbers(seasons) });
}

/// The titles a search answered with that are actually the one somebody opened.
///
/// An indexer answers a search for "Breaking Bad" with Better Call Saul's episode of that name
/// and with a film that has it in the title. Shown under the programme, those arrive as a sixth
/// season it never had - so the answer is narrowed to the titles whose names really are this
/// one, and only where nothing matches at all is the whole answer kept.
export function titlesOf(
  title: string,
  year: number | undefined,
  found: DiscoveredTitle[],
): DiscoveredTitle[] {
  const wanted = bare(title);

  // A prefix either way: "The Office" answers for "The Office US", and "Star Wars: Andor" for
  // "Andor". A name that merely contains it - "El Camino: A Breaking Bad Movie" - does not.
  const named = found.filter((candidate) => {
    const theirs = bare(candidate.title);

    return theirs.length > 0 && (theirs.startsWith(wanted) || wanted.startsWith(theirs));
  });

  const candidates = named.length > 0 ? named : found;

  // The catalogue knows the year and an indexer mostly does not, so a candidate that names one
  // and disagrees is a different work with the same name - but only where some candidate did
  // agree, since most of them say nothing at all.
  const dated = candidates.filter(
    (candidate) =>
      year === undefined || candidate.year === undefined || Math.abs(candidate.year - year) <= 1,
  );

  return dated.length > 0 ? dated : candidates;
}

/// A title with the things that differ between two writings of it taken out.
function bare(title: string): string {
  return title
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/// One heading's worth of versions, as the list of them is broken up for showing.
///
/// A programme comes back as several hundred postings: a few that hold everything, some that
/// hold a season, and the rest one episode each. Ungrouped that is a wall of release names -
/// and sorted by picture quality the seasons come out shuffled, which is worse than useless
/// for somebody looking for season three.
export interface VersionGroup {
  /// Stable key for the list: `complete`, `season:3`, `other`.
  key: string;

  /// The season this is about, where it is about one.
  season?: number;

  /// Whether this is the group that holds whole programmes rather than single seasons.
  isComplete: boolean;

  /// Everything shown under this heading, in the order it is shown: packs first, then the
  /// episodes by number.
  releases: DiscoveredRelease[];

  /// The episode numbers this season's postings cover between them.
  episodes: number[];

  /// One posting per episode, for the button that takes a whole season. Empty where a pack of
  /// this season was found - the pack is the better answer and it is listed right here - and
  /// where there are no loose episodes at all.
  wholeSeason: DiscoveredRelease[];
}

/// The versions of a title, grouped the way somebody reads them.
///
/// In order: what holds the whole programme, then each season by number, then everything whose
/// name said nothing about seasons - which is every version of every film.
export function groupVersions(releases: DiscoveredRelease[]): VersionGroup[] {
  const whole = releases.filter(
    (release) => release.contents?.isComplete || (release.contents?.seasons.length ?? 0) > 1,
  );

  const bySeason = new Map<number, { packs: DiscoveredRelease[]; episodes: DiscoveredRelease[] }>();

  for (const release of releases) {
    const contents = release.contents;

    if (!contents || whole.includes(release) || contents.scope === 'Unknown') {
      continue;
    }

    const season = contents.seasons[0] ?? 0;
    const group = bySeason.get(season) ?? { packs: [], episodes: [] };

    (contents.scope === 'Season' ? group.packs : group.episodes).push(release);
    bySeason.set(season, group);
  }

  const groups: VersionGroup[] = [];

  if (whole.length > 0) {
    groups.push({
      key: 'complete',
      isComplete: true,

      // The one that covers the most first; the server's order decides between equals.
      releases: [...whole].sort(
        (first, second) =>
          (second.contents?.seasons.length ?? 0) - (first.contents?.seasons.length ?? 0),
      ),
      episodes: [],
      wholeSeason: [],
    });
  }

  // Specials last, as on the title page: season one is where a programme starts, and "season 0"
  // in front of it is a numbering, not an order anybody watches in.
  for (const [season, { packs, episodes }] of [...bySeason.entries()].sort(
    (first, second) => (first[0] === 0 ? 1 : 0) - (second[0] === 0 ? 1 : 0) || first[0] - second[0],
  )) {
    // Inside a season: the packs, then the episodes by number. The server's order answers
    // "which version of this is best"; within one season the question is "which episode".
    const sorted = [...episodes].sort(
      (first, second) => (first.contents?.episodes[0] ?? 0) - (second.contents?.episodes[0] ?? 0),
    );

    groups.push({
      key: `season:${season}`,
      season,
      isComplete: false,
      releases: [...packs, ...sorted],
      episodes: [...new Set(episodes.flatMap((release) => release.contents?.episodes ?? []))].sort(
        (first, second) => first - second,
      ),
      wholeSeason: packs.length > 0 ? [] : onePerEpisode(episodes),
    });
  }

  // Everything that said nothing about seasons. For a film this is the whole list, which is
  // why it is a group rather than something left out.
  const rest = releases.filter((release) => (release.contents?.scope ?? 'Unknown') === 'Unknown');

  if (rest.length > 0) {
    groups.push({ key: 'other', isComplete: false, releases: rest, episodes: [], wholeSeason: [] });
  }

  return groups;
}

/// The fewest postings that cover every episode there is, taking the best of each.
///
/// Best is not decided here: the list arrives in the order the server put it in - the language
/// somebody asked for, then the picture - so the first posting that holds an episode is the one
/// to take. A posting that holds three episodes covers all three, and the two it made redundant
/// are not fetched again.
function onePerEpisode(releases: DiscoveredRelease[]): DiscoveredRelease[] {
  const covered = new Set<number>();
  const taken: DiscoveredRelease[] = [];

  const episodes = [...new Set(releases.flatMap((release) => release.contents?.episodes ?? []))].sort(
    (first, second) => first - second,
  );

  for (const episode of episodes) {
    if (covered.has(episode)) {
      continue;
    }

    const best = releases.find((release) => release.contents?.episodes.includes(episode));

    if (!best) {
      continue;
    }

    taken.push(best);

    for (const held of best.contents?.episodes ?? []) {
      covered.add(held);
    }
  }

  return taken;
}

/// One episode of a season put together from single postings: every version that holds it, and
/// the one taken unless somebody picks another.
export interface ComposedEpisode {
  episode: number;

  /// Every posting of this season that holds the episode, in the order the server put them in,
  /// with what is already in the library first.
  versions: DiscoveredRelease[];

  /// What is taken for it by default.
  chosen: DiscoveredRelease;
}

/// A season built out of the single episodes posted of it - what somebody wants when nobody
/// ever posted the season whole, or when the pack that was posted is not the version they want.
///
/// By default each episode gets the best posting of it, the same pick the button for a whole
/// season makes: one that holds two episodes covers both, so nothing is fetched twice. Where a
/// version of an episode is already in the library, that one is what is shown - the season is
/// then partly there, and says so.
export function composeSeason(group: VersionGroup): ComposedEpisode[] {
  const loose = group.releases.filter((release) => release.contents?.scope === 'Episode');
  const taken = new Map<number, DiscoveredRelease>();

  for (const release of onePerEpisode(loose)) {
    for (const episode of release.contents?.episodes ?? []) {
      if (!taken.has(episode)) {
        taken.set(episode, release);
      }
    }
  }

  return group.episodes.flatMap((episode) => {
    const holding = loose.filter((release) => release.contents?.episodes.includes(episode));

    // Stable, so among the ones in the library and among the rest the server's order stands.
    const versions = [...holding].sort((first, second) => Number(second.isAdded) - Number(first.isAdded));
    const chosen = versions.find((release) => release.isAdded) ?? taken.get(episode) ?? versions[0];

    return chosen ? [{ episode, versions, chosen }] : [];
  });
}

/// The episodes of a season that no posting holds, between the first and the last one some
/// posting does. A gap in the middle of a season is what somebody would otherwise find weeks
/// later, halfway through watching it.
export function missingEpisodes(composed: ComposedEpisode[]): number[] {
  const held = new Set(composed.map((entry) => entry.episode));
  const last = Math.max(0, ...held);
  const missing: number[] = [];

  for (let episode = 1; episode < last; episode++) {
    if (!held.has(episode)) {
      missing.push(episode);
    }
  }

  return missing;
}

/// What one group of versions is called: the whole programme, a season, or the rest.
export function groupHeading(group: VersionGroup, t: TFunction): string {
  if (group.isComplete) {
    return t('discover.contents.complete');
  }

  return group.season === undefined
    ? t('discover.versions.other')
    : t('discover.contents.season', { season: group.season });
}

/// The postings sorted by where they came from, so each provider is asked once: a batch is one
/// provider's work.
export function byProvider(releases: DiscoveredRelease[]): [string, DiscoveredRelease[]][] {
  const grouped = new Map<string, DiscoveredRelease[]>();

  for (const release of releases) {
    grouped.set(release.providerId, [...(grouped.get(release.providerId) ?? []), release]);
  }

  return [...grouped.entries()];
}

/// The episodes a group covers, written as a run: "1-10". Empty where it covers none.
export function episodesLabel(group: VersionGroup): string {
  return numbers(group.episodes);
}

/// A run of numbers written the way people write one: 1-5 rather than 1, 2, 3, 4, 5.
export function numbers(values: number[]): string {
  if (values.length === 0) {
    return '';
  }

  const runs: string[] = [];
  let start = values[0];
  let previous = values[0];

  for (const value of values.slice(1)) {
    if (value === previous + 1) {
      previous = value;

      continue;
    }

    runs.push(start === previous ? `${start}` : `${start}–${previous}`);
    start = value;
    previous = value;
  }

  runs.push(start === previous ? `${start}` : `${start}–${previous}`);

  return runs.join(', ');
}
